import { MediaDeliveryError } from './errors.js'
import { IMAGE_QUALITY, normalizeImagePolicy } from './image-policy.js'

export const DEFAULT_IMAGE_DELIVERY = Object.freeze({ s3_origins: Object.freeze(['https://media.voko.world']) })

/**
 * Source boundary. Only public managed raster images are transformable.
 * SVG, GIF, local assets and third-party images keep their delivery.
 * CloudFront hostnames alone do not imply an installed image transformer.
 * @param {string|null|undefined} source
 * @param {{s3_origins: readonly string[]}} config
 */
export function imageSource(source, config = DEFAULT_IMAGE_DELIVERY) {
  if (source === null || source === undefined || source === '') return { url: '', provider: 'direct' }
  if (typeof source !== 'string') throw new MediaDeliveryError('image source must be a URL string', 'invalid_base_url')
  if (!/^https?:\/\//i.test(source)) {
    if (/^(\/|data:image\/|blob:)/.test(source)) return { url: source, provider: 'direct' }
    throw new MediaDeliveryError(`unsupported image URL: ${source}`, 'invalid_base_url')
  }
  const url = new URL(source)
  if (url.username || url.password) throw new MediaDeliveryError('image URL must not contain credentials', 'invalid_base_url')
  if (!/\.(png|jpe?g|webp|avif|tiff?)$/i.test(url.pathname)) return { url: source, provider: 'direct' }
  let provider = 'direct'
  if (/(^|\.)oss-[a-z0-9-]+\.aliyuncs\.com$/i.test(url.hostname)) provider = 'oss'
  else if (config.s3_origins.includes(url.origin)) provider = 's3'
  if (provider !== 'direct' && ['OSSAccessKeyId', 'Signature', 'Expires', 'x-oss-signature', 'X-Amz-Signature', 'Policy'].some(key => url.searchParams.has(key))) {
    throw new MediaDeliveryError(`managed display image requires a public unsigned URL: ${url.origin}${url.pathname}`, 'signed_source')
  }
  if (provider !== 'direct' && (url.search !== '' || (provider === 's3' && url.pathname.startsWith('/fit-in/')))) {
    throw new MediaDeliveryError(`managed display image requires an untransformed source: ${url.origin}${url.pathname}`, 'transformed_source')
  }
  return { url: source, provider }
}

/** Pure provider adapter; no network or image bytes pass through the app. */
export function imageUrl(source, width) {
  if (source.provider === 'direct') return source.url
  const url = new URL(source.url)
  switch (source.provider) {
    case 'oss':
      url.searchParams.set('x-oss-process', `image/resize,w_${width},limit_1/format,webp/quality,Q_${IMAGE_QUALITY}`)
      break
    case 's3':
      // DIT v8.1.1 query edits cannot express quality or no-upscale; use its native path API.
      url.pathname = `/fit-in/${width}x0/filters:format(webp):quality(${IMAGE_QUALITY}):no_upscale()${url.pathname}`
      break
    default: throw new Error(`Unhandled image provider: ${source.provider}`)
  }
  return url.href
}

export function createImageDeliveryUrl(source, request, config = DEFAULT_IMAGE_DELIVERY) {
  return imageUrl(imageSource(source, config), normalizeImagePolicy(request).width)
}
